import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";

const navLinks = [
  { label: "私たちについて", href: "/about" },
  {
    label: "サービス",
    href: "#services",
    dropdown: [
      { label: "DX顧問", href: "/service/dx-consultant" },
      { label: "伴走型AI事務局サポート", href: "/service/ai-office" },
      { label: "AI・デジタル導入支援", href: "/service/ai-digital" },
      { label: "業務ツール・システム開発", href: "/service/business-tool" },
    ],
  },
  { label: "料金", href: "/pricing" },
  { label: "支援事例", href: "/cases" },
  { label: "DX・AI活用ノート", href: "#notes" },
  { label: "よくある質問", href: "#faq" },
];

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const handleNav = useCallback(
    (href: string) => {
      setMobileOpen(false);
      setDropdownOpen(false);
      if (href.startsWith("#")) {
        const el = document.querySelector(href);
        if (el) {
          el.scrollIntoView({ behavior: "smooth" });
        } else {
          navigate("/");
          setTimeout(() => {
            const retry = document.querySelector(href);
            retry?.scrollIntoView({ behavior: "smooth" });
          }, 300);
        }
      } else {
        navigate(href);
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    },
    [navigate]
  );

  const linkClass = isScrolled
    ? "text-foreground-800 hover:text-primary-600 hover:bg-background-100"
    : "text-white/85 hover:text-white hover:bg-white/10";

  const dropdownBtnClass = isScrolled
    ? "text-foreground-800 hover:text-primary-600 hover:bg-background-100"
    : "text-white/85 hover:text-white hover:bg-white/10";

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-background-50/95 backdrop-blur-md border-b border-background-200/70"
          : "bg-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <a
            href="/"
            onClick={(e) => {
              e.preventDefault();
              handleNav("/");
            }}
            className="flex items-center gap-2 group cursor-pointer"
          >
            <img
              src="https://public.readdy.ai/ai/img_res/1c3ecf48-285d-4989-a01a-b0386ca0101e.png"
              alt="ノーブルラボ"
              className={`h-9 md:h-10 w-auto object-contain transition-all duration-300 ${
                isScrolled ? "" : "brightness-0 invert"
              }`}
            />
          </a>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <div key={link.label} className="relative" ref={link.dropdown ? dropdownRef : undefined}>
                {link.dropdown ? (
                  <>
                    <button
                      onClick={() => setDropdownOpen(!dropdownOpen)}
                      onMouseEnter={() => setDropdownOpen(true)}
                      className={`flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-md transition-colors cursor-pointer whitespace-nowrap ${dropdownBtnClass}`}
                    >
                      {link.label}
                      <i
                        className={`ri-arrow-down-s-line transition-transform ${
                          dropdownOpen ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                    {dropdownOpen && (
                      <div
                        onMouseLeave={() => setDropdownOpen(false)}
                        className="absolute top-full left-0 mt-1 w-56 bg-background-50 border border-background-200/70 rounded-lg overflow-hidden shadow-lg"
                      >
                        {link.dropdown.map((item) => (
                          <a
                            key={item.label}
                            href={item.href}
                            onClick={(e) => {
                              e.preventDefault();
                              handleNav(item.href);
                            }}
                            className="block px-4 py-3 text-sm text-foreground-800 hover:bg-primary-50 hover:text-primary-700 transition-colors cursor-pointer"
                          >
                            {item.label}
                          </a>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <a
                    href={link.href}
                    onClick={(e) => {
                      e.preventDefault();
                      handleNav(link.href);
                    }}
                    className={`px-3 py-2 text-sm font-medium rounded-md transition-colors cursor-pointer whitespace-nowrap ${linkClass}`}
                  >
                    {link.label}
                  </a>
                )}
              </div>
            ))}
          </nav>

          {/* CTA + Mobile Toggle */}
          <div className="flex items-center gap-2">
            <a
              href="#contact"
              onClick={(e) => {
                e.preventDefault();
                handleNav("#contact");
              }}
              className="hidden md:inline-flex items-center px-4 py-2 text-sm font-medium rounded-md bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              お問い合わせ
            </a>
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className={`lg:hidden w-10 h-10 flex items-center justify-center rounded-md transition-colors cursor-pointer ${
                isScrolled
                  ? "hover:bg-background-100/50"
                  : "hover:bg-white/10"
              }`}
              aria-label="メニュー"
            >
              <i
                className={`ri-${mobileOpen ? "close" : "menu"}-line text-xl transition-colors ${
                  isScrolled ? "text-foreground-800" : "text-white"
                }`}
              />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-background-50/98 backdrop-blur-md border-t border-background-200/70 max-h-[80vh] overflow-y-auto">
          <div className="px-4 py-4 space-y-1">
            {navLinks.map((link) => (
              <div key={link.label}>
                {link.dropdown ? (
                  <>
                    <button
                      onClick={() => setDropdownOpen(!dropdownOpen)}
                      className="w-full flex items-center justify-between px-3 py-3 text-sm font-medium text-foreground-800 hover:bg-background-100 rounded-md cursor-pointer"
                    >
                      {link.label}
                      <i
                        className={`ri-arrow-down-s-line transition-transform ${
                          dropdownOpen ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                    {dropdownOpen && (
                      <div className="pl-4 space-y-1 mt-1">
                        {link.dropdown.map((item) => (
                          <a
                            key={item.label}
                            href={item.href}
                            onClick={(e) => {
                              e.preventDefault();
                              handleNav(item.href);
                            }}
                            className="block px-3 py-2.5 text-sm text-foreground-700 hover:text-primary-600 hover:bg-primary-50/50 rounded-md cursor-pointer"
                          >
                            {item.label}
                          </a>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <a
                    href={link.href}
                    onClick={(e) => {
                      e.preventDefault();
                      handleNav(link.href);
                    }}
                    className="block px-3 py-3 text-sm font-medium text-foreground-800 hover:bg-background-100 rounded-md cursor-pointer"
                  >
                    {link.label}
                  </a>
                )}
              </div>
            ))}
            <div className="pt-2">
              <a
                href="#contact"
                onClick={(e) => {
                  e.preventDefault();
                  handleNav("#contact");
                }}
                className="block w-full text-center px-4 py-3 text-sm font-medium rounded-md bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer"
              >
                お問い合わせ
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}